'use strict';

// Import Libraries
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import FocusTrap from 'focus-trap-react';
import { href, link_classes, link_flags } from './utils';
import { builderDispatch, WebUI } from '../_utils/ui';

const DEFAULT_ORDER_STATE = Object.freeze({
  storage_provider_present: false,
  logged_in_user_name: '',
});

const MobileSideMenu = ({ headerLinks, links, orderProp }) => {
  useEffect(() => {
    const builderDispatchHandler = builderDispatch.register((event) => {
      switch (event.type) {
        case WebUI.LOGIN:
          updateComponent();
          break;
        case WebUI.TOGGLE_MOBILE_MENU:
          setIsOpen((prev) => !prev);
          break;
      }
    });
    return () => {
      builderDispatch.unregister(builderDispatchHandler);
    };
  }, []);

  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const toggleHandler = () => {
      builderDispatch.dispatch({ type: WebUI.TOGGLE_MOBILE_MENU });
    };

    document.addEventListener('toggleMobileMenu', toggleHandler);
    return () => {
      document.removeEventListener('toggleMobileMenu', toggleHandler);
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('mobile-menu-open');
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    } else {
      document.body.classList.remove('mobile-menu-open');
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      setNavStack([]);
    }
  }, [isOpen]);

  const [navStack, setNavStack] = useState([]);

  const menuRef = useRef(null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.keyCode === 27) setIsOpen(false);
    };

    const handleOrientationChange = () => setIsOpen(false);

    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('orientationchange', handleOrientationChange);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    let touchStartX = 0;
    let touchStartY = 0;
    let isHorizontalSwipe = null;

    const handleTouchStart = (e) => {
      // Only track touches that begin inside the menu
      if (menuRef.current && !menuRef.current.contains(e.target)) return;
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      isHorizontalSwipe = null;
    };

    const handleTouchMove = (e) => {
      if (touchStartX === 0 && touchStartY === 0) return;
      const deltaX = e.touches[0].clientX - touchStartX;
      const deltaY = e.touches[0].clientY - touchStartY;

      // Determine swipe axis on first significant movement
      if (
        isHorizontalSwipe === null &&
        Math.abs(deltaX) + Math.abs(deltaY) > 5
      ) {
        isHorizontalSwipe = Math.abs(deltaX) > Math.abs(deltaY);
      }

      // Prevent vertical scroll from interfering when it's clearly a left swipe
      if (isHorizontalSwipe && deltaX < 0) {
        e.preventDefault();
      }
    };

    const handleTouchEnd = (e) => {
      if (touchStartX === 0 && touchStartY === 0) return;
      const deltaX = e.changedTouches[0].clientX - touchStartX;
      if (isHorizontalSwipe && deltaX < -50) {
        setIsOpen(false);
      }
      touchStartX = 0;
      touchStartY = 0;
      isHorizontalSwipe = null;
    };

    // Listen on document so child scroll containers don't swallow the events
    document.addEventListener('touchstart', handleTouchStart, {
      passive: true,
    });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isOpen]);

  const currentLinks =
    navStack.length > 0 ? navStack[navStack.length - 1].links : links;

  const handleCategoryClick = (e, link) => {
    if (link.sublinks && link.sublinks.length > 0) {
      e.preventDefault();
      setNavStack((prev) => [
        ...prev,
        { title: link.name, links: link.sublinks },
      ]);
    } else {
      setIsOpen(false);
    }
  };

  const handleBack = () => {
    setNavStack((prev) => prev.slice(0, -1));
  };

  const initialOrder = orderProp || DEFAULT_ORDER_STATE;

  const [order, setOrder] = useState(initialOrder);

  const [loggedIn, setLoggedIn] = useState(
    Boolean(initialOrder.logged_in_user_name),
  );

  useEffect(() => {
    const appOrder =
      typeof window !== 'undefined' ? window.App?.order : undefined;
    if (appOrder) {
      setOrder(appOrder);
      setLoggedIn(Boolean(appOrder.logged_in_user_name));
    } else if (typeof window !== 'undefined' && window.initialOrderProps) {
      setOrder(window.initialOrderProps);
      setLoggedIn(Boolean(window.initialOrderProps.logged_in_user_name));
    } else {
      setOrder(DEFAULT_ORDER_STATE);
      setLoggedIn(false);
    }
  }, []);

  const updateComponent = () => {
    const appOrder =
      typeof window !== 'undefined'
        ? window.App?.order || window.initialOrderProps
        : undefined;
    if (appOrder) {
      setOrder(appOrder);
      setLoggedIn(Boolean(appOrder.logged_in_user_name));
    } else {
      setOrder(DEFAULT_ORDER_STATE);
      setLoggedIn(false);
    }
  };

  const renderAccountTools = () => {
    const { store_finder: storeFinderUrl, help_page: helpPageUrl } =
      headerLinks;
    const utilityLinks = () => {
      if (!storeFinderUrl && !helpPageUrl) {
        return null;
      }

      return (
        <>
          <li className="sfinder">
            <a href={storeFinderUrl} role="button">
              <i className="skinny-icon pin"></i>Store Finder
            </a>
          </li>
          <li className="help">
            <a href={helpPageUrl} role="button">
              <i className="skinny-icon help"></i>Help
            </a>
          </li>
        </>
      );
    };

    const renderHeaderLinks = () => {
      if (loggedIn) {
        const {
          logged_in_links: {
            order_prints: orderPrintsUrl,
            user_albums: userAlbumsUrl,
          },
        } = headerLinks;

        const uploadLinks = () => {
          const storageProviderPresent = Boolean(
            order?.storage_provider_present,
          );
          if (!storageProviderPresent) {
            return null;
          }

          return (
            <>
              <li className="upload">
                <a href={orderPrintsUrl} role="button">
                  <i className="skinny-icon upload"></i>Upload Photos
                </a>
              </li>
              <li className="photos">
                <a href={userAlbumsUrl} role="button">
                  <i className="skinny-icon photos"></i>My Photos
                </a>
              </li>
            </>
          );
        };
        const userName = order?.logged_in_user_name;

        const {
          logged_in_links: {
            user_projects: userProjectsUrl,
            user_account: userAccountUrl,
            track_orders: trackOrdersUrl,
            logout: logoutUrl,
          },
        } = headerLinks;

        return (
          <>
            <span className="logged-in">Hello, {userName}</span>
            <ul>
              {uploadLinks()}
              <li className="projects">
                <a href={userProjectsUrl} role="button">
                  <i className="skinny-icon projects"></i>My Projects
                </a>
              </li>
              <li className="account">
                <a href={userAccountUrl} role="button">
                  <i className="skinny-icon account"></i>My Account
                </a>
              </li>
              <li className="orders">
                <a href={trackOrdersUrl} role="button">
                  <i className="skinny-icon orders"></i>Track Orders
                </a>
              </li>
              {utilityLinks()}
              <li className="sign-out">
                <a href={logoutUrl} role="button">
                  <i className="skinny-icon sign-out"></i>Sign Out
                </a>
              </li>
            </ul>
          </>
        );
      } else {
        const { new_account: newAccountUrl, store_name: storeName } =
          headerLinks;

        return (
          <>
            <span>Account</span>
            <ul>
              <li className="sign-in">
                <a href="#" data-login="" role="button">
                  <i className="skinny-icon anna sign-in"></i>Sign In
                </a>
              </li>
              <li className="create-account">
                <a href={newAccountUrl} role="button">
                  <i className="skinny-icon create-account"></i>
                  <span className="menu-link-label">
                    Create Account
                    <small className="d-block">
                      Get more out of {storeName}
                    </small>
                  </span>
                </a>
              </li>
              {utilityLinks()}
            </ul>
          </>
        );
      }
    };

    return (
      <li
        id="login-status-burger"
        className="category"
        onClick={() => setIsOpen(false)}>
        {renderHeaderLinks()}
      </li>
    );
  };

  if (!isMounted) return null;
  return createPortal(
    <>
      {isOpen && (
        <div className="mobile-menu-overlay" onClick={() => setIsOpen(false)} />
      )}
      <FocusTrap
        active={isOpen}
        focusTrapOptions={{
          clickOutsideDeactivates: true,
          returnFocusOnDeactivate: true,
        }}>
        <div
          id="mobile-side-menu"
          ref={menuRef}
          className={classNames('mobile-side-menu', 'left', { open: isOpen })}>
          <div className="menu-header">
            {navStack.length > 0 ? (
              <button className="menu-back" type="button" onClick={handleBack}>
                <i className="menu-chevron-back" />
                <span>Back</span>
              </button>
            ) : (
              <span className="menu-header-title">Menu</span>
            )}
            {navStack.length > 0 && (
              <span className="menu-header-current">
                {navStack[navStack.length - 1].title}
              </span>
            )}
            <button
              className="close"
              id="close-menu-button"
              type="button"
              aria-label="Close"
              onClick={() => setIsOpen(false)}>
              <i className="skinny-icon close" />
            </button>
          </div>
          <ul>
            {navStack.length === 0 && renderAccountTools()}
            <li
              className={classNames('category', {
                'border-top': navStack.length === 0,
              })}>
              {navStack.length === 0 && <span>Categories</span>}
              <ul>
                {currentLinks.map((link, i) => (
                  <li key={i} className={classNames(link_classes(link))}>
                    <a
                      href={
                        link.sublinks && link.sublinks.length > 0
                          ? '#'
                          : href(link)
                      }
                      role="button"
                      onClick={(e) => handleCategoryClick(e, link)}>
                      <span className="menu-link-label">
                        {link.name}
                        {link_flags(link)}
                      </span>
                      {link.sublinks && link.sublinks.length > 0 && (
                        <i className="menu-chevron" />
                      )}
                    </a>
                  </li>
                ))}
              </ul>
            </li>
          </ul>
        </div>
      </FocusTrap>
    </>,
    document.body,
  );
};

MobileSideMenu.propTypes = {
  headerLinks: PropTypes.object,
  links: PropTypes.array,
  orderProp: PropTypes.object,
};

MobileSideMenu.defaultProps = {
  headerLinks: null,
  links: [],
  orderProp: null,
};

export default MobileSideMenu;
